<template>
	<datatable
		:headers="headers"
		:value="filteredItems"
		status-key="stock-check"
		highlight-clicked
		selectable
		expandable
		clickable
		:loading
		@row-click="clickedRow"
		@update:items-selected="(selections) => (selectedItems = selections)">
		<template #row-depot="{ row }">
			<span v-if="statusParam(row, 'depot', 'stock-check')">
				{{ statusParam(row, 'depot', 'stock-check') }}
			</span>

			<span v-else>
				{{ row.depot }}
			</span>
		</template>

		<template #row-delivery_date="{ row }">
			{{ new Date(row.delivery_date).toLocaleDateString('en-gb') }}
		</template>

		<template #row-quantity_required="{ row }">
			{{ parseInt(row.quantity_required) }}
		</template>

		<template #row-quantity_confirmed="{ row }">
			{{ statusParam(row, 'quantity_confirmed', 'stock-check') }}
		</template>

		<template #row-tos="{ row }">
			<app-checkbox
				v-if="row.stock_check_status && row.stock_check_status.length > 0"
				v-model="row.stock_check_status[0].tos"
				wrapper=""
				name="tos"
				:true-value="1"
				:false-value="0"
				@click.stop
				@update:model-value="emit('changedItems', row.id)" />
		</template>

		<template #row-notes="{ row }">
			<notes-button
				v-if="row.stock_check_status && row.stock_check_status[0]"
				:status="row.stock_check_status[0]"
				@click="editing = row" />
		</template>
	</datatable>

	<edit-contract-item-note
		:item="editing"
		area="stock-check"
		:resolve-allowed="true"
		:resolve-on-save="true"
		@updated="emit('updatedItems')"
		@closed="editing = undefined" />
</template>

<script lang="ts" setup>
	import type { Header } from '@/data/types/datatable'
	import { ref, watch, computed } from 'vue'
	import NotesButton from '@/components/partials/cat2/NotesButton.vue'
	import type { ContractItem } from '@/data/schema/contract-item'
	import EditContractItemNote from '@/components/partials/cat2/EditContractItemNote.vue'
	import { statusParam } from '@/composables/contracts'
	import { filterItems } from '@/utils/contract-items'

	const selectedItems = ref<ContractItem[]>([])
	const clicked = ref()
	const editing = ref<ContractItem | undefined>(undefined)

	// Filter out the "changed" key from the items
	const filteredItems = computed(() => {
		return filterItems(props.items)
	})

	const props = defineProps({
		items: {
			type: Array<ContractItem>,
			default: []
		},
		pickRefs: Boolean,
		loading: Boolean
	})

	const headers = ref<Header[]>([
		{ value: 'stock_status', text: 'Status', props: { width: 100 } },
		{ value: 'item_number', text: 'Item No', props: { sortable: true, width: 150 } },
		{ value: 'description', text: 'Item Description', props: { sortable: true, width: 300 } },
		{ value: 'type', text: 'Type', props: { sortable: true, width: 100 } },
		{ value: 'depot', text: 'Depot', props: { sortable: true, width: 80 } },
		{ value: 'delivery_date', text: 'Del Date', props: { sortable: true, width: 100 } },
		{ value: 'quantity_required', text: 'Qty Req', props: { sortable: true } },
		{ value: 'quantity_confirmed', text: 'Qty Conf', props: { sortable: true } },
		{ value: 'tos', text: 'Direct to Site', props: { sortable: false } },
		{ value: 'notes', text: 'Notes', props: { sortable: false } }
	])

	// Conditionally add picking ref column
	const pickingRefCol = {
		value: 'picking_reference',
		text: 'Pick Ref',
		props: { sortable: true }
	}
	if (props.pickRefs) {
		headers.value.splice(2, 0, pickingRefCol)
	}

	// Events
	const emit = defineEmits(['clickedItem', 'selectedItems', 'updatedItems', 'changedItems'])

	function clickedRow(item: ContractItem) {
		// Check if the item is not a kit
		if (!item.kit_components) {
			clicked.value = item
			emit('clickedItem', item)
		}
	}

	watch(
		() => selectedItems.value,
		(newValue) => {
			emit('selectedItems', newValue)
		},
		{ deep: true }
	)
</script>
